import Phaser from 'phaser';
import { HitZone } from '@/objects/HitZone';
import { SentTrack } from '@/objects/SentTrack';
import { NoteTrack } from '@/objects/NoteTrack';
import type { MorseNoteSprite } from '@/objects/MorseNote';
import { InputManager } from '@/systems/InputManager';
import {
  ScoringSystem,
  type GameState,
  type ScoredQuality,
  type HitResult,
  type PenaltyResult,
  type TimingFeedback,
} from '@/systems/ScoringSystem';
import { PerformanceJudgeTracker } from '@/systems/performanceJudge';
import { THEME } from '@/config/theme';
import type { LaneLayout } from './LaneLayoutManager';
import type { InputBinding } from '@/types/multiplayer';
import { HIT_ZONE, SCORING_ZONES, type DifficultyConfig } from '@/utils/timing';
import type { MorseSymbol } from '@/utils/morse';
import { HitDetector } from '@/systems/HitDetector';
import type { VFXManager } from '@/objects/VFXManager';
import type { AudioManager } from '@/systems/AudioManager';

interface LaneHUD {
  scoreText: Phaser.GameObjects.Text;
  multiplierText: Phaser.GameObjects.Text;
  streakProgressBg: Phaser.GameObjects.Graphics;
  streakProgressFill: Phaser.GameObjects.Graphics;
  feedbackText: Phaser.GameObjects.Text;
  nameText: Phaser.GameObjects.Text | null;
}

export interface PlayerLaneOptions {
  layout: LaneLayout;
  playerName?: string;
  isSinglePlayer?: boolean;
  inputBinding: InputBinding;
  difficulty: DifficultyConfig;
  noteMessage: string;
  targetMessage: string;
  borderColor?: number;
  vfxManager?: VFXManager;
  audioManager?: AudioManager;
  onLetterDecoded?: (letter: string) => void;
  onScoreEvent?: (payload: ScoreUpdatePayload) => void;
  onLaneHit?: (event: LaneHitEvent) => void;
  onLanePenalty?: (event: LanePenaltyEvent) => void;
}

export interface LaneKeyDownResult {
  note: MorseNoteSprite | null;
  startOffsetPx: number | null;
  timingFeedback: TimingFeedback | null;
  shouldCancelPending: boolean;
}

export interface LaneKeyUpResult {
  note: MorseNoteSprite | null;
  endOffsetPx: number | null;
}

export interface LaneHitEvent {
  quality: ScoredQuality;
  showBreakFeedback: boolean;
  streak: number;
  previousStreak: number;
}

export type LanePenaltyType = 'whiff' | 'passed';

export interface LanePenaltyEvent {
  type: LanePenaltyType;
  showBreakFeedback: boolean;
  previousStreak: number;
}

export interface ScoreUpdatePayload {
  seat: number;
  score: number;
}

const LANE_HEADER_OFFSET_Y = 18;
const LANE_TEXT_OFFSET_Y = 18;
const STREAK_BAR_WIDTH = 150;
const STREAK_BAR_HEIGHT = 21; // 30% taller
const MULTIPLIER_GAP = 10;

export class PlayerLane {
  public readonly playerId: number;
  public readonly layout: LaneLayout;
  public readonly inputManager: InputManager;
  public readonly hitZone: HitZone;
  public readonly sentTrack: SentTrack;
  public readonly noteTrack: NoteTrack;
  private readonly scoringSystem: ScoringSystem;
  public activeTarget: MorseNoteSprite | null = null;
  public readonly hud: LaneHUD;
  private readonly isSinglePlayer: boolean;
  private readonly playerName: string;
  private readonly borderColor: number;
  private laneMask: Phaser.Display.Masks.GeometryMask | null = null;
  private laneFrame: Phaser.GameObjects.Graphics | null = null;
  private laneMaskShape: Phaser.GameObjects.Graphics | null = null;
  private hitZoneHeight: number;
  private countdownText: Phaser.GameObjects.Text | null = null;
  private countdownTimer: Phaser.Time.TimerEvent | null = null;
  private streakBarX = 0;
  private streakBarY = 0;
  private streakProgressTargetWidth = 0;
  private streakProgressCurrentWidth = 0;
  private readonly streakCornerRadius = 12;
  private readonly difficulty: DifficultyConfig;
  private readonly onScoreEvent?: (payload: ScoreUpdatePayload) => void;
  private readonly onLaneHit?: (event: LaneHitEvent) => void;
  private readonly onLanePenalty?: (event: LanePenaltyEvent) => void;
  private readonly vfxManager?: VFXManager;
  private readonly audioManager?: AudioManager;
  private readonly judgeTracker = new PerformanceJudgeTracker();
  private readonly hitDetector: HitDetector;

  get isLocal(): boolean {
    // Single player = always local
    // Multiplayer = local if we have a colorful border (not default grey)
    return this.isSinglePlayer || this.borderColor !== THEME.colors.borderHex;
  }

  constructor(private scene: Phaser.Scene, options: PlayerLaneOptions) {
    this.layout = options.layout;
    this.playerId = options.layout.playerId;
    this.difficulty = options.difficulty;
    this.hitDetector = new HitDetector(options.layout.hitZoneX, SCORING_ZONES);
    this.onScoreEvent = options.onScoreEvent;
    this.isSinglePlayer = options.isSinglePlayer ?? false;
    this.playerName = options.playerName ?? `P${this.playerId + 1}`;
    this.borderColor = options.borderColor ?? THEME.colors.borderHex;
    this.onLaneHit = options.onLaneHit;
    this.onLanePenalty = options.onLanePenalty;
    this.vfxManager = options.vfxManager;
    this.audioManager = options.audioManager;
    this.hitZoneHeight = this.layout.trackHeight * 0.63;
    const outerHitZoneHeight = this.layout.trackHeight * 0.84;

    this.createLaneFrame(this.borderColor, outerHitZoneHeight);

    const letterY = this.getLaneTextRowY();

    this.noteTrack = new NoteTrack(scene, {
      message: options.noteMessage,
      difficulty: options.difficulty,
      hitZoneX: options.layout.hitZoneX,
      trackY: options.layout.trackY,
      letterY,
      mask: this.laneMask ?? undefined,
    });

    this.inputManager = new InputManager(scene, options.inputBinding);
    this.hitZone = new HitZone(scene, {
      x: options.layout.hitZoneX,
      y: options.layout.trackY,
      height: this.hitZoneHeight,
    });

    this.scoringSystem = new ScoringSystem(this.noteTrack.getTotalNotes());

    this.sentTrack = new SentTrack(scene, {
      hitZoneX: options.layout.hitZoneX,
      trackY: options.layout.trackY,
      letterY,
      scrollSpeed: options.difficulty.scrollSpeed,
      fadeEndX: options.layout.fadeEndX,
      stopX: options.layout.accumulatedStartX,
      difficulty: options.difficulty,
      mask: this.laneMask ?? undefined,
      onLetterDecoded: options.onLetterDecoded,
      intendedMessage: options.targetMessage,
    });

    this.hud = this.createLaneHud();
    this.layoutMultiplierHud();
  }

  start(gameTime: number): void {
    this.noteTrack.start(gameTime);
    this.sentTrack.start();
    this.inputManager.enable();
  }

  stopInput(): void {
    this.inputManager.disable();
    this.sentTrack.forceFinalize();
    this.hitZone.stopPressPulse();
  }

  update(time: number, delta: number): void {
    this.noteTrack.update(time, delta);
    this.sentTrack.update(time, delta);
    this.animateStreakProgress();
  }

  updateHUD(state: GameState): void {
    this.hud.scoreText.setText(state.score.toLocaleString());

    const multiplierColors: Record<number, string> = {
      1: THEME.colors.textDark,
      2: THEME.colors.yellow,
      3: THEME.colors.cyan,
      4: THEME.colors.magenta,
    };
    this.hud.multiplierText.setText(`x${state.multiplier}`);
    this.hud.multiplierText.setColor(multiplierColors[state.multiplier] || THEME.colors.text);
    this.layoutMultiplierHud();
  }

  updateStreakProgress(): void {
    const state = this.scoringSystem.getState();
    const streak = state.streak;
    let start: number;
    let end: number;
    if (streak < 10) {
      start = 0; end = 10;
    } else if (streak < 25) {
      start = 10; end = 25;
    } else if (streak < 50) {
      start = 25; end = 50;
    } else {
      this.streakProgressTargetWidth = STREAK_BAR_WIDTH;
      return;
    }

    const progress = (streak - start) / (end - start);
    this.streakProgressTargetWidth = progress * STREAK_BAR_WIDTH;
  }

  resetStreakProgress(): void {
    this.streakProgressTargetWidth = 0;
    this.streakProgressCurrentWidth = 0;
    this.redrawStreakFill(this.hud.streakProgressFill, 0);
  }

  animateStreakProgress(): void {
    const target = this.streakProgressTargetWidth;
    const current = this.streakProgressCurrentWidth;
    const stepped = Phaser.Math.Linear(current, target, 0.18);
    this.streakProgressCurrentWidth = Math.abs(stepped - target) < 0.5 ? target : stepped;
    this.redrawStreakFill(this.hud.streakProgressFill, this.streakProgressCurrentWidth);
  }

  showStreakMilestone(streak: number): void {
    const milestoneText = this.scene.add.text(
      this.layout.hitZoneX, this.layout.trackY + 130,
      `${streak} STREAK!`,
      {
        fontFamily: THEME.fonts.pixelBold,
        fontSize: THEME.sizes.bodyLarge,
        color: THEME.colors.cyan,
        stroke: THEME.stroke.color,
        strokeThickness: THEME.stroke.thickness,
      }
    ).setOrigin(0.5);

    this.scene.tweens.add({
      targets: milestoneText,
      scale: { from: 0.6, to: 1.2 },
      alpha: { from: 1, to: 0 },
      duration: 900,
      ease: 'Back.easeOut',
      onComplete: () => milestoneText.destroy(),
    });
  }

  showFeedbackText(text: string, color: string): void {
    this.hud.feedbackText.setText(text);
    this.hud.feedbackText.setColor(color);
    this.hud.feedbackText.setScale(1.2);
    this.hud.feedbackText.setAlpha(1);

    this.scene.tweens.add({
      targets: this.hud.feedbackText,
      scale: 1,
      alpha: 0,
      duration: 400,
    });
  }

  destroy(): void {
    this.inputManager.destroy();
    this.sentTrack.destroy();
    this.hitZone.destroy();
    this.hud.scoreText.destroy();
    this.hud.multiplierText.destroy();
    this.hud.streakProgressBg.destroy();
    this.hud.streakProgressFill.destroy();
    this.hud.feedbackText.destroy();
    this.hud.nameText?.destroy();
    this.laneFrame?.destroy();
    this.laneMaskShape?.destroy();
    this.countdownText?.destroy();
    this.countdownTimer?.destroy();
  }

  private createLaneHud(): LaneHUD {
    const headerY = this.getLaneHeaderY();

    const scoreText = this.scene.add.text(this.layout.trackX, headerY, '0', {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0, 0.5);

    this.streakBarX = this.layout.hitZoneX - STREAK_BAR_WIDTH / 2;
    this.streakBarY = headerY;

    const streakProgressBg = this.scene.add.graphics();
    const streakProgressFill = this.scene.add.graphics();

    this.drawStreakProgressBg(streakProgressBg);
    this.redrawStreakFill(streakProgressFill, 0);

    const multiplierText = this.scene.add.text(
      this.layout.hitZoneX,
      headerY,
      'x1',
      {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.body,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
      }
    ).setOrigin(1, 0.5);

    const nameText = this.isSinglePlayer ? null : this.scene.add.text(
      this.layout.trackX + this.layout.trackWidth,
      headerY,
      this.playerName,
      {
        fontFamily: THEME.fonts.pixelBold,
        fontSize: THEME.sizes.body,
        color: THEME.colors.textMuted,
        stroke: THEME.stroke.color,
        strokeThickness: THEME.stroke.thickness,
      }
    ).setOrigin(1, 0.5);

    const feedbackY = (this.layout.trackY + this.layout.trackHeight / 2) + 48;
    const feedbackText = this.scene.add.text(this.layout.hitZoneX, feedbackY, '', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.primary,
    }).setOrigin(0.5);

    return {
      scoreText,
      multiplierText,
      streakProgressBg,
      streakProgressFill,
      feedbackText,
      nameText,
    };
  }

  private getLaneHeaderY(): number {
    const trackBottom = this.layout.trackY + this.layout.trackHeight / 2;
    return trackBottom + LANE_HEADER_OFFSET_Y;
  }

  private getLaneTextRowY(): number {
    const trackTop = this.layout.trackY - this.layout.trackHeight / 2;
    return trackTop - LANE_TEXT_OFFSET_Y;
  }

  private drawStreakProgressBg(graphics: Phaser.GameObjects.Graphics): void {
    graphics.clear();
    const y = this.streakBarY - STREAK_BAR_HEIGHT / 2;
    graphics.fillStyle(0x333333, 1);
    graphics.fillRoundedRect(this.streakBarX, y, STREAK_BAR_WIDTH, STREAK_BAR_HEIGHT, this.streakCornerRadius);
    graphics.lineStyle(2, THEME.colors.primaryHex, 1);
    graphics.strokeRoundedRect(this.streakBarX, y, STREAK_BAR_WIDTH, STREAK_BAR_HEIGHT, this.streakCornerRadius);
  }

  private redrawStreakFill(graphics: Phaser.GameObjects.Graphics, width: number): void {
    graphics.clear();
    if (width <= 0) return;
    const y = this.streakBarY - STREAK_BAR_HEIGHT / 2;
    graphics.fillStyle(THEME.colors.cyanHex, 1);
    graphics.fillRoundedRect(this.streakBarX, y, width, STREAK_BAR_HEIGHT, this.streakCornerRadius);
  }

  private createLaneFrame(borderColor: number, hitZoneHeight: number): void {
    const trackTop = this.layout.trackY - this.layout.trackHeight / 2;
    const baseTrackHeight = 110;
    const scale = this.layout.trackHeight / baseTrackHeight;
    const laneRadius = 14;

    const maskShape = this.scene.make.graphics({ x: 0, y: 0 });
    maskShape.fillStyle(0xffffff);
    maskShape.fillRoundedRect(this.layout.trackX, trackTop, this.layout.trackWidth, this.layout.trackHeight, laneRadius);
    this.laneMask = maskShape.createGeometryMask();
    this.laneMaskShape = maskShape;

    const graphics = this.scene.add.graphics();
    graphics.fillGradientStyle(
      THEME.colors.backgroundLightHex,
      THEME.colors.backgroundLightHex,
      THEME.colors.backgroundPanelHex,
      THEME.colors.backgroundPanelHex,
      0.95, 0.95, 0.9, 0.9
    );
    graphics.fillRoundedRect(this.layout.trackX, trackTop, this.layout.trackWidth, this.layout.trackHeight, laneRadius);

    graphics.lineStyle(2, borderColor, 1);
    graphics.strokeRoundedRect(this.layout.trackX, trackTop, this.layout.trackWidth, this.layout.trackHeight, laneRadius);

    graphics.lineStyle(1, THEME.colors.backgroundPanelHex);
    graphics.lineBetween(this.layout.trackX, this.layout.trackY, this.layout.trackX + this.layout.trackWidth, this.layout.trackY);

    graphics.fillStyle(THEME.colors.cyanHex, 0.25);
    const chevronSpacing = 120;
    const chevronHalfWidth = 28 * scale;
    const chevronInset = 12 * scale;
    const margin = 40;
    const gapHalf = 80;

    const gapStart = this.layout.hitZoneX - gapHalf;
    const gapEnd = this.layout.hitZoneX + gapHalf;
    const leftLimit = this.layout.trackX + margin;
    const rightLimit = this.layout.trackX + this.layout.trackWidth - margin;

    for (let i = 0; ; i++) {
      const offset = chevronSpacing / 2 + i * chevronSpacing;
      const leftCx = gapStart - offset;
      const rightCx = gapEnd + offset;

      if (leftCx < leftLimit || rightCx > rightLimit) break;

      graphics.fillTriangle(
        leftCx - chevronHalfWidth, this.layout.trackY,
        leftCx + chevronHalfWidth, trackTop + chevronInset,
        leftCx + chevronHalfWidth, trackTop + this.layout.trackHeight - chevronInset
      );

      graphics.fillTriangle(
        rightCx - chevronHalfWidth, this.layout.trackY,
        rightCx + chevronHalfWidth, trackTop + chevronInset,
        rightCx + chevronHalfWidth, trackTop + this.layout.trackHeight - chevronInset
      );
    }

    graphics.lineStyle(2, THEME.colors.cyanHex, 0.5);
    const hitZoneRadius = 10 * scale;
    graphics.strokeRoundedRect(
      this.layout.hitZoneX - HIT_ZONE.outerWidth / 2,
      this.layout.trackY - hitZoneHeight / 2,
      HIT_ZONE.outerWidth,
      hitZoneHeight,
      hitZoneRadius
    );

    this.laneFrame = graphics;
  }

  private layoutMultiplierHud(): void {
    const gap = MULTIPLIER_GAP;
    this.streakBarY = this.getLaneHeaderY();
    this.streakBarX = this.layout.hitZoneX - STREAK_BAR_WIDTH / 2;
    this.drawStreakProgressBg(this.hud.streakProgressBg);
    this.redrawStreakFill(this.hud.streakProgressFill, this.streakProgressCurrentWidth);
    const textWidth = this.hud.multiplierText.width;
    this.hud.multiplierText.setPosition(this.streakBarX - gap - textWidth / 2, this.streakBarY);
  }

  setTargetMessage(message: string): void {
    // For Telephone mode: append only the new letter
    // NoteTrack handles incoming letters now
    const lastChar = message[message.length - 1];
    if (lastChar) {
      this.noteTrack.appendLetter(lastChar);
    }
  }

  showMultiplierPulse(): void {
    this.scene.tweens.add({
      targets: this.hud.multiplierText,
      scale: { from: 1.2, to: 1 },
      duration: 200,
    });
  }

  showFeedbackForHit(quality: ScoredQuality): void {
    const feedbackText: Record<ScoredQuality, string> = {
      perfect: 'PERFECT!',
      good: 'GOOD!',
      ok: 'OK',
      bad: 'BAD',
    };

    const feedbackColors: Record<ScoredQuality, string> = {
      perfect: THEME.colors.hit.perfect,
      good: THEME.colors.hit.good,
      ok: THEME.colors.hit.ok,
      bad: THEME.colors.hit.bad,
    };

    this.showFeedbackText(feedbackText[quality], feedbackColors[quality]);
  }

  setHitZonePressed(pressed: boolean): void {
    if (pressed) {
      this.hitZone.startPressPulse();
    } else {
      this.hitZone.stopPressPulse();
    }
  }

  handleKeyDown(time: number): LaneKeyDownResult {
    this.sentTrack.startSymbol(time);
    const note = this.findNearestPendingNote();
    if (!note) {
      this.activeTarget = null;
      this.judgeTracker.cancelPending();
      return {
        note: null,
        startOffsetPx: null,
        timingFeedback: null,
        shouldCancelPending: true,
      };
    }

    this.activeTarget = note;
    const startOffset = note.getStartOffset(this.layout.hitZoneX);
    this.judgeTracker.recordKeydown({
      offsetPx: startOffset,
      symbol: note.symbol,
      letter: note.expectedChar,
    });
    const keydown = this.scoringSystem.recordKeydownTiming(startOffset);

    return {
      note,
      startOffsetPx: startOffset,
      timingFeedback: keydown.timing,
      shouldCancelPending: false,
    };
  }

  handleKeyUp(time: number, duration: number, symbol: MorseSymbol): LaneKeyUpResult {
    this.sentTrack.finalizeSymbol(time);

    const note = this.activeTarget;
    this.activeTarget = null;

    if (!note || note.outcome !== 'pending') {
      const penalty = this.scoringSystem.recordWhiff(this.difficulty.scrollSpeed);
      this.emitScoreUpdate();
      this.handlePenaltyResult(penalty, 'whiff');
      return {
        note: null,
        endOffsetPx: null,
      };
    }

    const endOffset = note.getEndOffset(this.layout.hitZoneX);
    const symbolMatches = symbol === note.symbol;
    const result = this.scoringSystem.scoreHit(endOffset, symbolMatches, note.getSpeedPxPerSec());
    this.judgeTracker.recordKeyup({
      offsetPx: endOffset,
      symbol: note.symbol,
      letter: note.expectedChar,
    });
    note.resolveAsHit();

    this.emitScoreUpdate();
    this.handleHitResult(result);

    return {
      note,
      endOffsetPx: endOffset,
    };
  }

  checkPassedNotes(): void {
    for (const note of this.noteTrack.getNotes()) {
      if (note.outcome !== 'pending') continue;
      const endOffset = note.getEndOffset(this.layout.hitZoneX);
      if (endOffset < -SCORING_ZONES.ok) {
        const wasActiveTarget = this.activeTarget === note;
        if (wasActiveTarget) {
          this.activeTarget = null;
        }
        note.resolveAsUnhit();
        this.judgeTracker.cancelPending();
        const result = this.scoringSystem.recordPassedUnhit();
        this.emitScoreUpdate();
        this.handlePenaltyResult(result, 'passed');
      }
    }
  }

  private findNearestPendingNote(): MorseNoteSprite | null {
    return this.hitDetector.findNearestPendingNote(this.noteTrack.getNotes()) as MorseNoteSprite | null;
  }


  private emitScoreUpdate(): void {
    if (!this.onScoreEvent) return;
    const score = this.scoringSystem.getState().score;
    this.onScoreEvent({ seat: this.playerId, score });
  }

  /**
   * Shows "3, 2, 1, GO!" countdown centered within this lane.
   * Only shows for local lanes. Returns promise that resolves when complete.
   */
  showCountdown(): Promise<void> {
    if (!this.isLocal) return Promise.resolve();
    return this.runCountdownAnimation();
  }

  /**
   * For online games: starts countdown so "GO!" appears at startAt time.
   * Triggers the 3-second countdown ~3 seconds before startAt.
   */
  showCountdownAt(startAt: number): Promise<void> {
    if (!this.isLocal) return Promise.resolve();

    const delayMs = Math.max(0, startAt - Date.now() - 3000);

    return new Promise(resolve => {
      if (delayMs > 0) {
        this.scene.time.delayedCall(delayMs, () => {
          this.runCountdownAnimation().then(resolve);
        });
      } else {
        this.runCountdownAnimation().then(resolve);
      }
    });
  }

  private runCountdownAnimation(): Promise<void> {
    return new Promise(resolve => {
      this.countdownText = this.scene.add.text(
        this.layout.hitZoneX,
        this.layout.trackY,
        '3',
        {
          fontFamily: THEME.fonts.pixelBold,
          fontSize: THEME.sizes.titleLarge,
          color: THEME.colors.primary,
          stroke: THEME.stroke.color,
          strokeThickness: THEME.stroke.thicknessThick,
        }
      ).setOrigin(0.5).setDepth(100);

      let count = 3;
      this.countdownTimer = this.scene.time.addEvent({
        delay: 1000,
        callback: () => {
          count--;
          if (count > 0) {
            this.countdownText?.setText(count.toString());
            this.scene.tweens.add({
              targets: this.countdownText,
              scale: { from: 1.5, to: 1 },
              duration: 500,
            });
          } else if (count === 0) {
            this.countdownText?.setText('GO!');
            this.scene.tweens.add({
              targets: this.countdownText,
              scale: { from: 1.5, to: 1 },
              alpha: 0,
              duration: 500,
              onComplete: () => {
                this.countdownText?.destroy();
                this.countdownText = null;
              },
            });
          } else {
            this.countdownTimer?.destroy();
            this.countdownTimer = null;
            resolve();
          }
        },
        repeat: 3,
      });
    });
  }

  public addTotalNotes(delta: number): void {
    this.scoringSystem.addTotalNotes(delta);
  }

  public getResults(): ReturnType<ScoringSystem['getResults']> {
    return this.scoringSystem.getResults();
  }

  public getJudgeResults(): ReturnType<PerformanceJudgeTracker['getResults']> {
    return this.judgeTracker.getResults();
  }

  public getStreak(): number {
    return this.scoringSystem.getState().streak;
  }

  public getMaxStreak(): number {
    return this.scoringSystem.getState().maxStreak;
  }

  private handleHitResult(result: HitResult): void {
    const state = this.scoringSystem.getState();

    // Show feedback text
    this.showFeedbackForHit(result.quality);

    if (result.quality !== 'bad') {
      this.hitZone.updateStreak(state.streak);
      this.updateStreakProgress();

      switch (result.quality) {
        case 'perfect': this.hitZone.flashSuccess(); break;
        case 'good': this.hitZone.flashGood(); break;
        case 'ok': this.hitZone.flashOk(); break;
      }

      if (result.milestoneReached) {
        this.showStreakMilestone(state.streak);
        this.resetStreakProgress();
      }

      // VFX for good hits
      this.vfxManager?.showHitEffect(this.layout.hitZoneX, this.layout.trackY, result.quality);
      this.vfxManager?.updateStreakEffect(state.streak, this.layout.hitZoneX, this.layout.trackY);
    } else {
      this.hitZone.flashBad();
      this.hitZone.reset();
      this.resetStreakProgress();

      // VFX/audio for bad hits
      this.vfxManager?.showHitEffect(this.layout.hitZoneX, this.layout.trackY, result.quality);
      this.vfxManager?.updateStreakEffect(0, this.layout.hitZoneX, this.layout.trackY);
      if (result.showBreakFeedback) {
        this.vfxManager?.showComboBreak();
      }
      this.audioManager?.playSfx('whiff');
    }

    this.updateHUD(state);
    if (state.multiplier > 1) {
      this.showMultiplierPulse();
    }
    this.onLaneHit?.({
      quality: result.quality,
      showBreakFeedback: result.showBreakFeedback,
      streak: state.streak,
      previousStreak: result.previousStreak,
    });
  }

  private handlePenaltyResult(result: PenaltyResult, type: LanePenaltyType): void {
    const state = this.scoringSystem.getState();

    this.hitZone.flashBad();
    this.hitZone.reset();
    this.resetStreakProgress();

    // Show feedback text for whiff
    if (type === 'whiff') {
      this.showFeedbackText('WHIFF', THEME.colors.hit.bad);
    }

    // VFX
    if (type === 'passed') {
      this.vfxManager?.showPassedEffect(this.layout.hitZoneX, this.layout.trackY);
    }
    this.vfxManager?.updateStreakEffect(0, this.layout.hitZoneX, this.layout.trackY);
    if (result.showBreakFeedback) {
      this.vfxManager?.showComboBreak();
    }

    // Audio
    this.audioManager?.playSfx('whiff');

    this.updateHUD(state);
    if (state.multiplier > 1) {
      this.showMultiplierPulse();
    }
    this.onLanePenalty?.({
      type,
      showBreakFeedback: result.showBreakFeedback,
      previousStreak: result.previousStreak,
    });
  }
}
